import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDownRight } from "lucide-react";
import { WhatsAppIcon } from "./Logo";
import { BRAND, waLink, telLink, HERO_IMAGE } from "../lib/brand";

const HEADLINE = ["Property Services", "Built on Trust."];

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.35 } },
};
const lineChild = {
  hidden: { y: "115%" },
  show: {
    y: "0%",
    transition: { duration: 1.05, ease: [0.16, 1, 0.3, 1] },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: 0.9 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const STATS = [
  { k: "25+", v: "Years of trust" },
  { k: "1,200+", v: "Families served" },
  { k: "100%", v: "Clear titles" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative pt-28 pb-12 sm:pt-32 sm:pb-16 lg:pt-40 lg:pb-24 overflow-hidden"
      data-testid="hero-section"
    >
      {/* soft gold ambience */}
      <div className="pointer-events-none absolute -top-32 -right-32 h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,rgba(197,160,89,0.18),transparent_65%)]" />
      <div className="pointer-events-none absolute top-1/3 -left-40 h-[420px] w-[420px] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.10),transparent_65%)]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
          {/* Left */}
          <div>
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="flex items-center gap-3 mb-7"
            >
              <span className="h-px w-10 bg-[#c5a059]" />
              <span className="text-xs tracking-[0.34em] uppercase text-[#9a7b38] font-semibold">
                Hisar · Haryana · Since a Generation
              </span>
            </motion.div>

            <motion.h1
              variants={lineParent}
              initial="hidden"
              animate="show"
              className="font-serif font-semibold tracking-tight text-[#131311] leading-[0.98] text-5xl sm:text-6xl lg:text-[5.2rem]"
            >
              {HEADLINE.map((line, i) => (
                <span className="reveal-mask" key={i}>
                  <motion.span variants={lineChild} className="block">
                    {i === 1 ? (
                      <span className="text-gold-gradient italic">{line}</span>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </motion.h1>

            <motion.p
              variants={fadeUp}
              custom={1}
              initial="hidden"
              animate="show"
              className="mt-7 max-w-xl text-base lg:text-lg text-[#52514c] leading-relaxed"
            >
              {BRAND.tagline}. From prime plots and builder floors to farmhouses,
              commercial spaces and agricultural land — we help Hisar families and
              investors buy, sell and rent with complete transparency.
            </motion.p>

            <motion.div
              variants={fadeUp}
              custom={2}
              initial="hidden"
              animate="show"
              className="mt-9 flex flex-col sm:flex-row gap-3"
            >
              <a
                href={waLink("Hello Laxmi Jiwan Properties! I'm interested in a property in Hisar. Could you share some options?")}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-[#25d366] text-white px-7 py-4 text-sm font-semibold shadow-[0_12px_30px_rgba(37,211,102,0.28)] hover:bg-[#1eae53] transition-all duration-300 hover:-translate-y-0.5"
                data-testid="hero-whatsapp-cta"
              >
                <WhatsAppIcon className="h-5 w-5" />
                Chat on WhatsApp
              </a>
              <a
                href={telLink}
                className="group inline-flex items-center justify-center gap-2.5 rounded-full border border-[#131311] text-[#131311] px-7 py-4 text-sm font-semibold hover:bg-[#131311] hover:text-[#faf9f5] transition-all duration-300"
                data-testid="hero-phone-cta"
              >
                <Phone className="h-4 w-4" strokeWidth={2} />
                {BRAND.phoneDisplay}
              </a>
            </motion.div>

            <motion.div
              variants={fadeUp}
              custom={3}
              initial="hidden"
              animate="show"
              className="mt-10 flex items-center gap-6 sm:gap-8"
            >
              {STATS.map((s) => (
                <div key={s.v}>
                  <div className="font-serif text-3xl lg:text-4xl font-semibold text-[#131311]">
                    {s.k}
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#8a8578] mt-1">
                    {s.v}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right — framed parallax image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="relative rounded-[2rem] p-2 bg-gradient-to-br from-[#f0d98a] via-[#c5a059] to-[#9a7b38] shadow-[0_40px_80px_-30px_rgba(19,19,17,0.45)]">
              <div className="relative overflow-hidden rounded-[1.7rem] aspect-[4/5]">
                <motion.img
                  src={HERO_IMAGE}
                  alt="Luxury property in Hisar"
                  style={{ y, scale }}
                  className="absolute inset-0 h-[115%] w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#131311]/45 via-transparent to-transparent" />
              </div>
            </div>

            {/* floating badge */}
            <motion.div
              initial={{ opacity: 0, y: 20, x: -10 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="absolute -bottom-6 -left-4 sm:-left-8 bg-[#faf9f5] rounded-2xl border border-[#e7e3d7] shadow-[0_20px_50px_-20px_rgba(19,19,17,0.4)] px-5 py-4 flex items-center gap-4"
            >
              <div className="h-11 w-11 rounded-full bg-[#131311] grid place-items-center text-[#f0d98a]">
                <ArrowDownRight className="h-5 w-5" />
              </div>
              <div>
                <div className="font-serif text-xl font-semibold text-[#131311] leading-none">
                  Sector 33
                </div>
                <div className="text-[11px] uppercase tracking-[0.16em] text-[#8a8578] mt-1">
                  Hisar, Haryana
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
